import React from 'react';
import { Row, Col, Form, Button, Card } from 'react-bootstrap';
import ConditionRow from '../../common/ConditionRow';
import { standardInputStyles, standardLabelStyles } from '../../../utils/conditionInputStyles';

/** Object validation: compare previous-record field value (requires on_update trigger). */
const OLD_VALUE_OPERATORS = [
  { value: 'old_=', label: 'Old =' },
  { value: 'old_!=', label: 'Old !=' },
  { value: 'old_is_null', label: 'Old Is Null' },
  { value: 'old_is_not_null', label: 'Old Is Not Null' },
  { value: 'old_<', label: 'Old <' },
  { value: 'old_>', label: 'Old >' },
  { value: 'old_<=', label: 'Old <=' },
  { value: 'old_>=', label: 'Old >=' },
  { value: 'old_IN', label: 'Old IN' },
  { value: 'old_NOT IN', label: 'Old NOT IN' },
  { value: 'old_contains', label: 'Old Contains' },
  { value: 'old_starts_with', label: 'Old Starts With' },
  { value: 'old_ends_with', label: 'Old Ends With' },
];

const OLD_VALUE_PICKLIST_OPERATORS = [
  { value: 'old_=', label: 'Old =' },
  { value: 'old_!=', label: 'Old !=' },
  { value: 'old_is_null', label: 'Old Is Null' },
  { value: 'old_is_not_null', label: 'Old Is Not Null' },
  { value: 'old_IN', label: 'Old IN' },
  { value: 'old_NOT IN', label: 'Old NOT IN' },
];

/**
 * Sharing-rule criteria: same field / operator / value UX as workflow criteria,
 * without "Evaluate On" or isChanged (sharing applies to record visibility, not create/update events).
 */
export default function SharingRuleCriteriaConfig({
  criteriaList,
  onAdd,
  onDelete,
  onChange,
  objectFields,
  readOnly,
  disabled = false,
  /** When true, add "Is Changed" (same as workflows); useful for object validation on update. */
  includeIsChangedOperator = false,
  /** When true, add Old = / Old IN / etc. (previous value); object validation only. */
  includeOldValueOperators = false,
  /** Tighter rows and padding (e.g. object validation modal). */
  compact = false,
}) {
  const operators = [
    { value: '=', label: '=' },
    { value: '!=', label: '!=' },
    { value: 'is_null', label: 'Is Null' },
    { value: 'is_not_null', label: 'Is Not Null' },
    { value: '<', label: '<' },
    { value: '>', label: '>' },
    { value: '<=', label: '<=' },
    { value: '>=', label: '>=' },
    { value: 'IN', label: 'IN' },
    { value: 'NOT IN', label: 'NOT IN' },
    { value: 'contains', label: 'Contains' },
    { value: 'starts_with', label: 'Starts With' },
    { value: 'ends_with', label: 'Ends With' },
    ...(includeIsChangedOperator ? [{ value: 'isChanged', label: 'Is Changed' }] : []),
    ...(includeOldValueOperators ? OLD_VALUE_OPERATORS : []),
  ];

  const picklistOperators = [
    { value: '=', label: '=' },
    { value: '!=', label: '!=' },
    { value: 'is_null', label: 'Is Null' },
    { value: 'is_not_null', label: 'Is Not Null' },
    { value: 'IN', label: 'IN' },
    { value: 'NOT IN', label: 'NOT IN' },
    ...(includeIsChangedOperator ? [{ value: 'isChanged', label: 'Is Changed' }] : []),
    ...(includeOldValueOperators ? OLD_VALUE_PICKLIST_OPERATORS : []),
  ];

  const cardBodyClass = compact ? 'p-2' : 'p-3';
  const cardMb = compact ? 'mb-2' : 'mb-3';
  const delPb = compact ? 'pb-1' : 'pb-3';

  return (
    <div className={compact ? 'mt-1' : 'mt-2'}>
      {criteriaList != null &&
        criteriaList.length > 0 &&
        criteriaList.map((criteria, index) => (
          <Card key={index} className={`${cardMb} border`}>
            <Card.Body className={cardBodyClass}>
              <Row className="align-items-end gy-1">
                <Col md={10}>
                  <ConditionRow
                    condition={criteria}
                    fields={objectFields}
                    onChange={onChange}
                    index={index}
                    readOnly={readOnly}
                    options={{
                      fieldKey: 'fieldName',
                      nameKey: 'field_name',
                      labelKey: 'field_label',
                      customOperators: operators,
                      picklistOperators,
                      fieldColSize: 4,
                      operatorColSize: 3,
                      valueColSize: 5,
                    }}
                  />
                </Col>
                {!readOnly && (
                  <Col md={2} className={`d-flex align-items-end ${delPb}`}>
                    <Button
                      variant="outline-danger"
                      onClick={() => onDelete(index)}
                      size="sm"
                      disabled={readOnly}
                      className="w-100"
                    >
                      Delete
                    </Button>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        ))}

      {criteriaList == null || criteriaList.length === 0 ? (
        <div
          className={`text-center text-muted border rounded bg-light ${
            compact ? 'py-2' : 'py-3'
          }`}
        >
          <p className="mb-0 small">
            No criteria yet. Add criteria to limit which records this rule applies to.
          </p>
        </div>
      ) : null}

      {!readOnly && (
        <Button
          variant="outline-primary"
          onClick={onAdd}
          disabled={readOnly || disabled}
          title={disabled ? 'Select an object first' : ''}
          className={compact ? 'mt-1' : 'mt-2'}
          size="sm"
        >
          <i className="fa fa-plus me-2" />
          Add Criteria
        </Button>
      )}
    </div>
  );
}

/** Criteria logic: combine builder rows with AND/OR, or use a custom expression only. */
export function SharingCriteriaLogicSelect({ value, onChange, readOnly, compact = false }) {
  const normalized = ['AND', 'OR', 'CUSTOM'].includes(value) ? value : 'AND';
  return (
    <Form.Group className={compact ? 'mb-1' : 'mb-3'}>
      <Form.Label className={compact ? 'small fw-semibold mb-1' : 'fw-semibold'} style={standardLabelStyles}>
        Criteria Logic
      </Form.Label>
      <Form.Select
        value={normalized}
        onChange={(e) => onChange(e.target.value)}
        disabled={readOnly}
        style={standardInputStyles}
        size={compact ? 'sm' : undefined}
      >
        <option value="AND">AND (All Rows Must Match)</option>
        <option value="OR">OR (Any Row Can Match)</option>
        <option value="CUSTOM">Custom Logic (Expression)</option>
      </Form.Select>
    </Form.Group>
  );
}
